import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subscribe } from '../utils/eventBus';

const NotificationContext = createContext();

// Notification types
export const NOTIFICATION_TYPES = {
  ORDER_SUCCESS: 'order_success',
  ORDER_UPDATE: 'order_update',
  PROMO: 'promo',
  SYSTEM: 'system',
};

// Storage key
const NOTIFICATIONS_KEY = 'techbegin_notifications';

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notifications from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(NOTIFICATIONS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.read).length);
      }
    } catch (e) {
      console.error('Failed to load notifications', e);
    }
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(notifications));
      setUnreadCount(notifications.filter(n => !n.read).length);
    } catch (e) {
      console.error('Failed to save notifications', e);
    }
  }, [notifications]);

  // Add a new notification
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString(),
      read: false,
      ...notification,
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep max 50 notifications

    // Show browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon1.ico',
        });
      } catch (e) {
        // Ignore notification errors
      }
    }

    return newNotification;
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
  }, []);

  // Delete a notification
  const deleteNotification = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.filter(n => n.id !== notificationId)
    );
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Subscribe to order success events
  useEffect(() => {
    const unsubscribe = subscribe('order:success', (orderData) => {
      addNotification({
        type: NOTIFICATION_TYPES.ORDER_SUCCESS,
        title: '🎉 Order Placed Successfully!',
        message: `Your order #${orderData?.orderNumber || 'N/A'} has been confirmed. Total: ₹${((orderData?.totalAmount || 0) / 100).toFixed(2)}`,
        data: orderData,
      });
    });

    return () => unsubscribe && unsubscribe();
  }, [addNotification]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // We'll request permission when user interacts with notifications
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        requestNotificationPermission,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
